// Pricing migration 32 (ADO-3985), T13 and T19: what the multi-buy deal view (the dialog, the
// deal in the side cart and the product page's deal section) says and draws for one deal.
//
// Pure. Everything comes from the deal read's progress (amended AC3: the arithmetic stays in
// pricing; the web reads `pooledQuantity`, `freeUnitsAtCurrent` and `unitsToNext`, never counts):
// - The slots are the deal's smallest buy quantity; the empty ones are `unitsToNext`, all of
//   them filled exactly at a threshold (the confirmed tick-slot property). Above 6 slots, or when
//   the next rise is further off than the smallest buy (several tiers), the progress bar replaces
//   them (board 09): full exactly at a threshold, as the slots are (08, 09), otherwise filled as the
//   pooled count of the next threshold.
// - D-2 exception (recorded in the story): whether the count sits exactly at a threshold (the
//   next rise is one smallest buy away) and how many times a one-tier standard deal is reached
//   (free units over the tier's free quantity) are read off the server's progress here.
// - WOW copy is boards 06 to 10's, verbatim. Standard entries (R1 05s, 08s, 10s) show the deal
//   reached with the BWG note, no free case and no FREE chip as earned (the boards draw the chip
//   solid: the story's held designer question 7).
// - After the shop dropped below a deal (`lost`), the partway copy says how to get it back
//   (R3 21w-b, 21s-b).
// - Out-of-stock lines are left out of the lines count (R6 07u); with none left the deal reads
//   "This deal can't be completed right now".
// - A deal with no free-unit tier shows its description and no picking progress (D-11).
// Copy the boards do not draw follows their own patterns and is listed in the story for the
// designer ("Copy the boards don't draw").

import type { MultibuyDeal, MultibuyDealTier } from '../api/multibuy-deals.client';
import { dealDatesText, lowestTier, tiersText } from './multibuyDealText';

/** Above this many cases the slots give way to the progress bar (board 09: "Up to 6 cases:
 *  tick slots. Above 6 … the progress bar"). */
export const MAX_TICK_SLOTS = 6;

export interface DealViewContext {
  /** The entry is WOW: free cases go into the cart. Otherwise BWG adds the free stock. */
  readonly wow: boolean;
  /** The shop dropped below this deal and has not reached it again (R3). */
  readonly lost: boolean;
  /** The deal's lines the shop can pick from now (out-of-stock lines left out, R6). */
  readonly availableLines: number;
}

export type DealMeter =
  | { readonly kind: 'slots'; readonly count: number; readonly filled: number }
  /** `fill`: 0 to 1; full exactly at a threshold, else the pooled count over the next threshold
   *  ("8 of 12"). */
  | { readonly kind: 'bar'; readonly fill: number };

export interface DealViewProgress {
  /** The progress message, announced politely as it changes. */
  readonly message: string;
  /** `success` (green) once the deal is reached. */
  readonly tone: 'neutral' | 'success';
  readonly meter: DealMeter | null;
  readonly chip: { readonly label: string; readonly earned: boolean } | null;
}

export interface DealView {
  readonly header: {
    /** `Multi-buy · 22 Oct – 22 Nov`, or `Multi-buy · Ends 22 Nov` on WOW (D-13). */
    readonly pill: string;
    readonly title: string;
    /** Under a description title, in promotion red (R5 06n): `Buy 4, get 1 free · Buy 10, get 3
     *  free`. `null` with no description: the tier text is then the title. */
    readonly tiersLine: string | null;
    /** The dialog's subtitle (06): `Buy any 4 cases from the 3 lines below`. */
    readonly subtitle: string | null;
    /** The panel's subtitle (15): `Buy any 4 from the 3 lines below`. */
    readonly panelSubtitle: string | null;
  };
  readonly progress: DealViewProgress | null;
  /** The reached tag under the slots: Label Tag Tone=Success (08, 10, R1 08s). */
  readonly tag: { readonly label: string } | null;
  /** The standard entry's Info banner once the deal is reached (R1 08s). */
  readonly banner: string | null;
  /** The line under the free product's name, after its code; dashed until reached. */
  readonly freeBox: { readonly line: string; readonly reached: boolean } | null;
  readonly listLabel: string | null;
  readonly hint: string | null;
  readonly footer: string;
}

/** BWG's note at the threshold on a standard entry (R1, placeholder until BWG confirms: BQ-1). */
export const BWG_NOTE = 'Free stock for this deal is added by BWG, not shown in this cart';

function ordinal(value: number): string {
  const lastTwo = value % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${value}th`;
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[value % 10] ?? 'th';
  return `${value}${suffix}`;
}

const plural = (count: number, one: string, many: string): string => (count === 1 ? one : many);

const linesWord = (lines: number): string => `${lines} ${plural(lines, 'line', 'lines')}`;

/** How many times a standard deal is reached, read from its free units; known only for a deal
 *  with one tier (several tiers give no single count). */
function timesReached(deal: MultibuyDeal, free: number): number | null {
  if (deal.tiers.length !== 1) return null;
  const tier = deal.tiers[0] as MultibuyDealTier;
  return Number.isInteger(free / tier.freeQuantity) ? free / tier.freeQuantity : null;
}

function reachedWords(times: number | null): string {
  if (times === null || times === 1) return 'deal reached';
  if (times === 2) return 'deal reached twice';
  return `deal reached ${times} times`;
}

function headerOf(deal: MultibuyDeal, availableLines: number): DealView['header'] {
  const dates = dealDatesText(deal);
  const pill = dates === null ? 'Multi-buy' : `Multi-buy · ${dates}`;
  if (deal.kind === 'DESCRIPTION_ONLY') {
    return { pill, title: deal.description, tiersLine: null, subtitle: null, panelSubtitle: null };
  }
  const { buyQuantity: buy, freeQuantity: free } = lowestTier(deal);
  const lines = linesWord(availableLines);
  // With no line to pick from there is nothing below to buy from.
  const pickable = availableLines > 0;
  return {
    pill,
    title: deal.description ?? `Buy ${buy}, get ${free} FREE`,
    tiersLine: deal.description === undefined ? null : tiersText(deal),
    subtitle: pickable ? `Buy any ${buy} cases from the ${lines} below` : null,
    panelSubtitle: pickable ? `Buy any ${buy} from the ${lines} below` : null,
  };
}

export function dealView(deal: MultibuyDeal, context: DealViewContext): DealView {
  const header = headerOf(deal, context.availableLines);
  const picked = deal.progress.pooledQuantity;
  if (deal.kind === 'DESCRIPTION_ONLY') {
    return {
      header,
      progress: null,
      tag: null,
      banner: null,
      freeBox: null,
      listLabel: null,
      hint: null,
      footer: `${picked} picked`,
    };
  }

  const { buyQuantity: buy, freeQuantity: freePerSet } = lowestTier(deal);
  const free = deal.progress.freeUnitsAtCurrent;
  const toNext = deal.progress.unitsToNext ?? null;
  const reached = free > 0;
  const atThreshold = reached && toNext === buy;
  const { wow } = context;
  const times = wow ? null : timesReached(deal, free);

  const freeBox =
    deal.freeProduct === undefined
      ? null
      : {
          reached,
          line: wow
            ? free >= 2
              ? `${free} cases · set by the deal · added automatically`
              : atThreshold && picked === buy
                ? 'set by the deal · added to your cart automatically'
                : 'set by the deal · added automatically'
            : free >= 2
              ? `${free} cases · set by the deal · BWG adds them when it processes your order`
              : 'set by the deal · BWG adds it when it processes your order',
        };

  if (context.availableLines === 0) {
    return {
      header,
      progress: {
        message: "This deal can't be completed right now",
        tone: 'neutral',
        meter: null,
        chip: null,
      },
      tag: null,
      banner: null,
      freeBox,
      listLabel: null,
      hint: null,
      footer: `${picked} picked`,
    };
  }

  const nextThreshold = deal.progress.nextThreshold ?? null;
  const meter: DealMeter | null =
    toNext === null || nextThreshold === null
      ? null
      : buy > MAX_TICK_SLOTS || toNext > buy
        ? { kind: 'bar', fill: atThreshold ? 1 : Math.min(1, Math.max(0, picked / nextThreshold)) }
        : { kind: 'slots', count: buy, filled: atThreshold ? buy : buy - toNext };
  const chip = {
    label: wow && free >= 2 ? `${free} FREE` : 'FREE',
    earned: wow && reached,
  };

  let message: string;
  if (picked === 0) {
    message =
      freePerSet === 1
        ? `Pick any ${buy} cases from the list below — the ${ordinal(buy + 1)} is free`
        : `Pick any ${buy} cases from the list below — get ${freePerSet} free`;
  } else if (!reached) {
    const more = toNext ?? 0;
    const goal = context.lost
      ? wow
        ? 'to get your free case back'
        : 'to reach the deal again'
      : freePerSet === 1
        ? 'to get your free case'
        : `to get your ${freePerSet} free cases`;
    message = `${picked} of ${buy} picked — pick ${more} more ${goal}`;
  } else if (wow) {
    message =
      atThreshold && picked === buy && free === 1
        ? `${buy} of ${buy} picked — your free case has been added to your cart`
        : `${picked} picked — ${free} ${plural(free, 'free case', 'free cases')} added to your cart`;
  } else {
    message =
      atThreshold && picked === buy
        ? `${buy} of ${buy} picked — deal reached`
        : `${picked} picked — ${reachedWords(atThreshold ? times : null)}`;
  }

  const tag = !reached
    ? null
    : wow
      ? {
          label:
            atThreshold && picked === buy && free === 1
              ? 'Free case added'
              : `${free} ${plural(free, 'free case', 'free cases')} added · pick ${toNext ?? buy} more for another`,
        }
      : { label: times !== null && times >= 2 ? `Deal reached ×${times}` : 'Deal reached' };

  return {
    header,
    progress: { message, tone: reached ? 'success' : 'neutral', meter, chip },
    tag,
    banner: !wow && reached ? BWG_NOTE : null,
    freeBox,
    listLabel: reached
      ? `Your ${picked} cases`
      : context.availableLines === 1
        ? `Buy any ${buy} from this line`
        : `Buy any ${buy} from these ${linesWord(context.availableLines)}`,
    hint: reached
      ? null
      : wow
        ? `You can add fewer than ${buy} — you just won't get the free case.`
        : `You can add fewer than ${buy} — you just won't reach the deal.`,
    footer: !reached
      ? `${picked} picked`
      : wow
        ? `${picked} picked + ${free} free`
        : `${picked} picked · ${reachedWords(atThreshold ? times : null)}`,
  };
}
